/**
 * Turning a pile of finished clips into a posting schedule.
 *
 * The clips are the product; posting them is the work that actually gets them
 * seen, and it is the part a streamer does badly — twenty clips posted in one
 * afternoon reach fewer people than the same twenty spread over a fortnight,
 * and nobody wants to open a scheduler twenty times.
 *
 * So the streamer says the shape of it — these clips, two a day, Tuesday to
 * Saturday, at six and nine in the evening — and this works out which clip
 * lands in which slot. The result is handed to Postly as a CSV, which is the
 * one thing it ingests that needs no API key and no beta access.
 *
 * ## No timezones, deliberately
 *
 * Slots are local wall-clock: a date and a time, with no offset and no instant.
 * That is not laziness, it is the only answer that cannot be quietly wrong.
 * "Tuesday at 18:00" means the same thing to the streamer, to their audience,
 * and to the scheduler reading the file — whereas converting to UTC here means
 * guessing which timezone the scheduler will interpret the result in, and being
 * wrong by hours for anybody who travels or lives through a daylight-saving
 * change mid-schedule.
 *
 * Day arithmetic goes through `Date.UTC` for the same reason: adding 24 hours
 * to a local date lands on the wrong day twice a year.
 *
 * Pure. No clock, no storage, no formatting beyond the two strings.
 */
/** The longest schedule worth planning in one go. */
export const MAX_HORIZON_DAYS = 60;
/** And the default, which is a fortnight of posting from one stream's clips. */
export const DEFAULT_HORIZON_DAYS = 14;
export const WEEKDAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
const DAY_MS = 86_400_000;
/** `YYYY-MM-DD` to a UTC instant at midnight, or null. */
function parseDate(date) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date ?? "").trim());
    if (!m)
        return null;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (mo < 1 || mo > 12 || d < 1 || d > 31)
        return null;
    const at = Date.UTC(y, mo - 1, d);
    // Rejects the 31st of February rather than rolling it into March, which would
    // silently move somebody's whole schedule.
    const back = new Date(at);
    if (back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d)
        return null;
    return at;
}
function formatDate(at) {
    const d = new Date(at);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
/** `HH:mm`, or null. Minutes are not rounded: 18:07 is a choice. */
export function parseTime(time) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(String(time ?? "").trim());
    if (!m)
        return null;
    const [h, min] = [Number(m[1]), Number(m[2])];
    if (h > 23 || min > 59)
        return null;
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}
/**
 * Cleans a set of rules into ones that can be honoured.
 *
 * Done separately from planning so a caller can show the streamer what their
 * rules actually mean before anything is scheduled — "Tuesday and Thursday, two
 * a day" is worth reading back, and an invalid time silently ignored is how a
 * schedule comes out half the length somebody expected.
 */
export function sanitiseRules(rules) {
    const weekdays = [...new Set((rules.weekdays ?? []).filter((d) => d >= 0 && d <= 6))].sort();
    const times = [...new Set((rules.times ?? []).map(parseTime).filter((t) => !!t))]
        // Sorted, because the order times are typed in is not an intention and two
        // posts arriving out of order within a day reads as a bug.
        .sort();
    const horizonDays = Math.min(MAX_HORIZON_DAYS, Math.max(1, Math.round(rules.horizonDays ?? DEFAULT_HORIZON_DAYS)));
    const startDate = parseDate(rules.startDate) === null ? "" : rules.startDate.trim();
    return {
        weekdays,
        times,
        startDate,
        horizonDays,
        // A schedule needs somewhere to start and at least one time of day. No
        // weekdays is not invalid: it means every day.
        valid: startDate !== "" && times.length > 0,
    };
}
/**
 * Places clips into slots, in the order given.
 *
 * In order rather than shuffled or sorted by score: the caller chose the order
 * — usually best first, because the first post is the one with the most reach —
 * and re-deciding it here would make the preview disagree with the list it was
 * built from.
 */
export function planPosts(clipIds, rules) {
    const r = sanitiseRules(rules);
    const start = parseDate(r.startDate);
    if (!r.valid || start === null) {
        return { slots: [], unplaced: [...clipIds], lastDate: null };
    }
    const slots = [];
    const queue = [...clipIds];
    let lastDate = null;
    for (let day = 0; day < r.horizonDays && queue.length > 0; day++) {
        const at = start + day * DAY_MS;
        const weekday = new Date(at).getUTCDay();
        // An empty list means every day, which is why this is not a membership test
        // against a default of all seven: "every day" and "all seven chosen" should
        // behave the same, and they do.
        if (r.weekdays.length > 0 && !r.weekdays.includes(weekday))
            continue;
        const date = formatDate(at);
        for (const time of r.times) {
            const clipId = queue.shift();
            if (!clipId)
                break;
            slots.push({ clipId, date, time });
            lastDate = date;
        }
    }
    return { slots, unplaced: queue, lastDate };
}
/**
 * What the schedule amounts to, in a sentence.
 *
 * Worth having because the rules are easy to set and hard to picture: five
 * clips, two a day, three days a week is a week and a half, and nobody works
 * that out from three form fields.
 */
export function describePlan(plan, rules) {
    const r = sanitiseRules(rules);
    if (!r.valid)
        return "Pick a start date and at least one time of day.";
    if (plan.slots.length === 0)
        return "Nothing selected to post.";
    const perDay = r.times.length;
    const days = r.weekdays.length === 0 || r.weekdays.length === 7
        ? "every day"
        : r.weekdays.map((d) => WEEKDAY_NAMES[d].slice(0, 3)).join(", ");
    const head = `${plan.slots.length} post${plan.slots.length === 1 ? "" : "s"}, ` +
        `${perDay} a day on ${days}, ` +
        `from ${plan.slots[0].date} to ${plan.lastDate}.`;
    if (plan.unplaced.length === 0)
        return head;
    // Said rather than dropped. A schedule that quietly omits four clips is worse
    // than one that says it ran out of room.
    return (`${head} ${plan.unplaced.length} more will not fit in ${r.horizonDays} days — ` +
        `add a time, add a day, or post those next time.`);
}
/**
 * One RFC 4180 field.
 *
 * Quoted whenever it could possibly need it rather than only when it does. A
 * caption is free text written by a streamer and will contain commas, quotes
 * and newlines; a field that is usually fine and occasionally shifts every
 * column right is the kind of bug that posts the wrong video.
 */
function csvField(value) {
    return `"${String(value ?? "").replace(/"/g, '""')}"`;
}
/**
 * A CSV Postly can bulk-schedule from.
 *
 * `due_date` is what carries the schedule, and the column names are Postly's
 * rather than ours. The date format is `YYYY-MM-DD HH:mm`, which is the
 * ISO-ish shape its documentation asks for, in local wall-clock — see the note
 * at the top of this file about why there is no offset on it.
 *
 * A header row always, even with no rows: an empty file with a header imports
 * as "nothing to do", while an empty file imports as an error somebody has to
 * work out.
 */
export function toPostlyCsv(rows) {
    const header = ["content", "media_url", "due_date"].join(",");
    const body = rows.map((row) => [
        csvField(row.caption),
        csvField(row.mediaUrl),
        csvField(`${row.date} ${row.time}`),
    ].join(","));
    // CRLF, which is what RFC 4180 specifies and what spreadsheet software on
    // Windows expects from a file it is handed.
    return [header, ...body].join("\r\n") + "\r\n";
}
/**
 * A caption, from what is actually known about the clip.
 *
 * Deliberately plain and deliberately not generated by a model. What the
 * product knows is the game, the video reacted to, and how hard the crowd
 * reacted — and a line built from those is true, which matters more here than
 * clever: this is going out under the streamer's name, and they can edit every
 * one of them in the scheduler.
 */
export function defaultCaption(clip) {
    if (clip.title)
        return clip.title;
    if (clip.gameName)
        return clip.gameName;
    if (clip.videoTitle)
        return `Reacting to ${clip.videoTitle}`;
    // Nothing known about it. An empty caption is better than a made-up one: the
    // scheduler will show it blank and the streamer will fill it in, which is
    // what they would have done to a guess anyway.
    return "";
}
//# sourceMappingURL=post-plan.js.map